import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { HttpMonitoringApi } from "../../interface/monitoring-api";
import type { userState } from "../../interface/MainInterface";
import { buildMonitoringDetailUrl, pressReleaseItemId } from "./monitoringDashboardUtils";
import { formatMonitoringActivePeriod, isMonitoringExpired } from "./monitoringActiveWindowUtils";

type MonitoringStatus = "ACTIVE" | "PAUSED";
type MonitoringType = "PRESS_RELEASE" | "KEYWORD";
type NotificationAudience = "ME" | "ALL" | "CUSTOM";
type TypeFilter = "ALL" | MonitoringType;
type StatusFilter = "ALL" | MonitoringStatus | "RUNNING";
type RuntimeStatusTone = "running" | "ready" | "paused";
type MonitoringListLayout = "table" | "compact-table" | "card";

interface NewMonitoringListPageProps {
  userState?: userState;
}

interface MonitoringListItem {
  id: string;
  name: string;
  description: string;
  monitoringType: MonitoringType;
  status: MonitoringStatus;
  isRunning: boolean;
  intervalMinutes: number;
  sentimentEnabled: boolean;
  notificationEnabled: boolean;
  notificationAudience: NotificationAudience;
  notificationRecipientUserCount: number;
  retentionDays: number;
  revisionTrackingEnabled: boolean;
  notifyOnNegative: boolean;
  notifyOnRevisionDetected: boolean;
  lastExecutedAt?: string;
  activeUntilDate: string;
  activeUntil: string;
  deactivatedAt: string;
  deactivationReason: string;
  createdAt: string;
}

interface DashboardSettingState {
  visibleItemIds: string[];
  itemOrder: string[];
  cardColors: Record<string, unknown>;
  articleLimits: Record<string, unknown>;
  articleDisplayFilters: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

const monitoringApi = new HttpMonitoringApi();
const newBadgeWindowMs = 7 * 24 * 60 * 60 * 1000;
const figmaColumnTemplate = "90px minmax(300px, 1fr) 120px 280px 240px 140px 140px 160px";
const figmaMinTableWidth = 1470;
const tableHorizontalPadding = 48;
const tableLayoutBudget = figmaMinTableWidth + tableHorizontalPadding;
const compactTableMinWidth = 874;
const compactTableLayoutBudget = compactTableMinWidth + tableHorizontalPadding;
const compactColumnTemplate = "70px minmax(132px, 1.25fr) 72px minmax(154px, 1.2fr) minmax(128px, 1fr) 96px 96px 126px";
const monitoringListIconBaseUrl = `${process.env.PUBLIC_URL}/assets/monitoring-list`;
const monitoringListIcons = {
  add: `${monitoringListIconBaseUrl}/icon-add.png`,
  search: `${monitoringListIconBaseUrl}/icon-search.png`,
  selectArrow: `${monitoringListIconBaseUrl}/icon-select-arrow.png`,
};

const getNotificationAudience = (item: any): NotificationAudience => {
  const recipients = item.notificationRecipients || item.recipients || [];
  if (item.notificationRecipientScope === "CUSTOM" || recipients.includes("CUSTOM")) return "CUSTOM";
  if (item.notificationRecipientScope === "ALL" || recipients.includes("ALL")) return "ALL";
  return "ME";
};

const getNotificationAudienceLabel = (item: Pick<MonitoringListItem, "notificationAudience" | "notificationRecipientUserCount">) => {
  if (item.notificationAudience === "CUSTOM") return `지정 ${item.notificationRecipientUserCount}명`;
  return item.notificationAudience === "ALL" ? "홍보실 전체" : "나에게만";
};

const toFiniteNumber = (value: unknown, fallback: number) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const clampNumber = (value: unknown, fallback: number, min: number, max: number) =>
  Math.min(max, Math.max(min, toFiniteNumber(value, fallback)));

const normalizeDate = (value?: string) => {
  if (!value) return "";
  if (/^\d{14}/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}T${value.slice(8, 10)}:${value.slice(10, 12)}:${value.slice(12, 14)}+09:00`;
  }
  return value;
};

const parseNormalizedDate = (value?: string) => {
  const normalized = normalizeDate(value);
  if (!normalized) return null;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatRecentRun = (value?: string) => {
  const date = parseNormalizedDate(value);
  if (!date) return value ? normalizeDate(value) : "기록 없음";
  const parts = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${lookup.month}.${lookup.day}.${lookup.hour}:${lookup.minute}`;
};

const isNewMonitoring = (createdAt?: string, referenceAt = new Date()) => {
  const createdDate = parseNormalizedDate(createdAt);
  if (!createdDate) return false;
  const ageMs = referenceAt.getTime() - createdDate.getTime();
  return ageMs >= 0 && ageMs <= newBadgeWindowMs;
};

const getRuntimeStatus = (item: Pick<MonitoringListItem, "status" | "isRunning">) => {
  if (item.status !== "ACTIVE") return { label: "중지", tone: "paused" as const };
  if (item.isRunning) return { label: "실행 중", tone: "running" as const };
  return { label: "대기", tone: "ready" as const };
};

const getTypeLabel = (type: MonitoringType) =>
  type === "PRESS_RELEASE" ? "보도자료" : "키워드";

const mapMonitoringListItem = (item: any): MonitoringListItem => ({
  id: item.monitoringId || item.id,
  name: item.title || item.name || "이름 없는 모니터링",
  description: item.description || "",
  monitoringType: item.monitoringType === "PRESS_RELEASE" ? "PRESS_RELEASE" : "KEYWORD",
  status: item.status === "PAUSED" ? "PAUSED" : "ACTIVE",
  isRunning: item.isRunning === true,
  intervalMinutes: toFiniteNumber(item.runIntervalMinutes ?? item.intervalMinutes, 15),
  sentimentEnabled: item.sentimentEnabled !== false,
  notificationEnabled: item.notificationEnabled !== false && item.notificationPolicy !== "NONE",
  notificationAudience: getNotificationAudience(item),
  notificationRecipientUserCount: Array.isArray(item.notificationRecipientUserIds) ? item.notificationRecipientUserIds.length : 0,
  retentionDays: clampNumber(item.articleRetentionDays ?? item.retentionDays, 3, 1, 7),
  revisionTrackingEnabled: false,
  notifyOnNegative: item.notifyOnNegative === true,
  notifyOnRevisionDetected: item.notifyOnRevisionDetected === true,
  lastExecutedAt: item.lastExecutedAt || "",
  activeUntilDate: item.activeUntilDate || (item.activeUntil ? String(item.activeUntil).slice(0, 10) : ""),
  activeUntil: item.activeUntil || "",
  deactivatedAt: item.deactivatedAt || "",
  deactivationReason: item.deactivationReason || "",
  createdAt: item.createdAt || "",
});

const normalizeStringList = (value: unknown) =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0) : [];

const normalizeRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? { ...(value as Record<string, unknown>) } : {};

const normalizeDashboardSetting = (value: unknown): DashboardSettingState => {
  const raw = value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
  return {
    ...raw,
    visibleItemIds: normalizeStringList(raw.visibleItemIds),
    itemOrder: normalizeStringList(raw.itemOrder),
    cardColors: normalizeRecord(raw.cardColors),
    articleLimits: normalizeRecord(raw.articleLimits),
    articleDisplayFilters: normalizeRecord(raw.articleDisplayFilters),
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : undefined,
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : undefined,
  };
};

const hasSavedDashboardSetting = (setting: DashboardSettingState | null) =>
  Boolean(setting?.createdAt || setting?.updatedAt);

const dedupePreservingOrder = (values: string[]) => {
  const seen = new Set<string>();
  return values.filter((value) => {
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const getDashboardItemId = (item: MonitoringListItem) =>
  item.monitoringType === "PRESS_RELEASE" ? pressReleaseItemId : item.id;

const normalizeDashboardItemIds = (ids: string[], items: MonitoringListItem[]) => {
  const pressReleaseIds = new Set(
    items
      .filter((item) => item.monitoringType === "PRESS_RELEASE")
      .map((item) => item.id)
  );
  return dedupePreservingOrder(
    ids.map((id) => (id === pressReleaseItemId || pressReleaseIds.has(id) ? pressReleaseItemId : id))
  );
};

const buildDefaultDashboardItemIds = (items: MonitoringListItem[]) =>
  dedupePreservingOrder(items.map(getDashboardItemId));

const getBaseDashboardVisibleIds = (setting: DashboardSettingState, items: MonitoringListItem[]) =>
  hasSavedDashboardSetting(setting)
    ? normalizeDashboardItemIds(setting.visibleItemIds, items)
    : buildDefaultDashboardItemIds(items);

const getSaveableDashboardOrder = (setting: DashboardSettingState, items: MonitoringListItem[]) => {
  const normalizedOrder = normalizeDashboardItemIds(setting.itemOrder, items);
  return normalizedOrder.length > 0 ? normalizedOrder : buildDefaultDashboardItemIds(items);
};

const useMeasuredTableLayout = () => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<MonitoringListLayout>("card");

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const updateLayout = (width: number) => {
      const nextLayout: MonitoringListLayout = width >= tableLayoutBudget
        ? "table"
        : width >= compactTableLayoutBudget
          ? "compact-table"
          : "card";
      setLayout((currentLayout) => (currentLayout === nextLayout ? currentLayout : nextLayout));
    };
    const updateFromPanel = () => updateLayout(panel.getBoundingClientRect().width);

    updateFromPanel();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateFromPanel);
      return () => window.removeEventListener("resize", updateFromPanel);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries.find((candidate) => candidate.target === panel);
      if (entry) updateLayout(entry.contentRect.width);
    });
    observer.observe(panel);

    return () => observer.disconnect();
  }, []);

  return { panelRef, layout };
};

const NewMonitoringListPage = ({ userState }: NewMonitoringListPageProps) => {
  const navigate = useNavigate();
  const { panelRef, layout } = useMeasuredTableLayout();
  const isTableLayout = layout !== "card";
  const userId = userState?.id ?? "";
  const [items, setItems] = useState<MonitoringListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("ALL");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [statusDialogItem, setStatusDialogItem] = useState<MonitoringListItem | null>(null);
  const [dashboardSetting, setDashboardSetting] = useState<DashboardSettingState | null>(null);
  const [dashboardSettingLoading, setDashboardSettingLoading] = useState(false);
  const [dashboardSettingError, setDashboardSettingError] = useState<string | null>(null);
  const [dashboardToggleError, setDashboardToggleError] = useState<string | null>(null);
  const [dashboardUpdatingId, setDashboardUpdatingId] = useState<string | null>(null);
  const statusDialogExpired = statusDialogItem ? isMonitoringExpired(statusDialogItem) : false;

  useEffect(() => {
    let ignore = false;

    const loadItems = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const response = await monitoringApi.post({ command: "list_monitorings" });
        if (ignore) return;
        if (response?.code === "200") {
          setItems(((response.response?.monitorings ?? []) as any[]).map(mapMonitoringListItem));
        } else {
          setLoadError(response?.response?.error_msg ?? "모니터링 목록을 불러오지 못했습니다.");
          setItems([]);
        }
      } catch (error) {
        if (!ignore) {
          setLoadError(error instanceof Error ? error.message : "모니터링 목록을 불러오지 못했습니다.");
          setItems([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadItems();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadDashboardSetting = async () => {
      if (!userId) {
        setDashboardSetting(null);
        setDashboardSettingError("사용자 정보를 확인하지 못해 대시보드 표시 설정을 불러오지 못했습니다.");
        return;
      }

      setDashboardSettingLoading(true);
      setDashboardSettingError(null);
      setDashboardToggleError(null);
      try {
        const response = await monitoringApi.post({ command: "get_dashboard_setting", userId });
        if (ignore) return;
        if (response?.code !== "200") {
          throw new Error(response?.response?.error_msg ?? "대시보드 표시 설정을 불러오지 못했습니다.");
        }
        setDashboardSetting(normalizeDashboardSetting(response.response?.dashboardSetting));
      } catch (error) {
        if (!ignore) {
          setDashboardSetting(null);
          setDashboardSettingError(error instanceof Error ? error.message : "대시보드 표시 설정을 불러오지 못했습니다.");
        }
      } finally {
        if (!ignore) setDashboardSettingLoading(false);
      }
    };

    loadDashboardSetting();
    return () => {
      ignore = true;
    };
  }, [userId]);

  const filteredItems = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return items.filter((item) => {
      const runtimeStatus = getRuntimeStatus(item);
      const matchesType = typeFilter === "ALL" || item.monitoringType === typeFilter;
      const matchesStatus =
        statusFilter === "ALL" ||
        item.status === statusFilter ||
        (statusFilter === "RUNNING" && runtimeStatus.tone === "running");
      const matchesSearch =
        !query ||
        [item.name, item.description, getTypeLabel(item.monitoringType)]
          .some((value) => value.toLowerCase().includes(query));

      return matchesType && matchesStatus && matchesSearch;
    });
  }, [items, searchText, statusFilter, typeFilter]);

  const dashboardVisibleIds = useMemo(() => {
    if (!dashboardSetting) return new Set<string>();
    return new Set(getBaseDashboardVisibleIds(dashboardSetting, items));
  }, [dashboardSetting, items]);

  const handleStatusToggleConfirm = async () => {
    if (!statusDialogItem) return;

    const target = statusDialogItem;
    const nextStatus: MonitoringStatus = target.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    if (nextStatus === "ACTIVE" && isMonitoringExpired(target)) {
      setLoadError("기간 만료로 중지된 모니터링은 종료일을 수정한 뒤 활성화할 수 있습니다.");
      setStatusDialogItem(null);
      return;
    }
    const command = nextStatus === "PAUSED" ? "pause_monitoring" : "resume_monitoring";

    setStatusUpdatingId(target.id);
    try {
      const response = await monitoringApi.post({
        command,
        monitoringId: target.id,
      });
      if (response?.code !== "200") {
        throw new Error(response?.response?.error_msg ?? "상태를 변경하지 못했습니다.");
      }

      const updated: MonitoringListItem = response?.response?.monitoring
        ? mapMonitoringListItem(response.response.monitoring)
        : { ...target, status: nextStatus, isRunning: nextStatus === "ACTIVE" ? false : target.isRunning };

      setItems((previous) =>
        previous.map((item) => (item.id === target.id ? { ...item, ...updated } : item))
      );
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "상태를 변경하지 못했습니다.");
    } finally {
      setStatusUpdatingId(null);
      setStatusDialogItem(null);
    }
  };

  const handleDashboardToggle = async (item: MonitoringListItem, checked: boolean) => {
    if (!dashboardSetting || !userId) {
      setDashboardToggleError("대시보드 표시 설정을 저장할 수 없습니다.");
      return;
    }

    const dashboardItemId = getDashboardItemId(item);
    const baseVisibleIds = getBaseDashboardVisibleIds(dashboardSetting, items);
    const nextVisibleIds = checked
      ? dedupePreservingOrder([...baseVisibleIds, dashboardItemId])
      : baseVisibleIds.filter((id) => id !== dashboardItemId);
    const nextSetting: DashboardSettingState = {
      ...dashboardSetting,
      visibleItemIds: nextVisibleIds,
      itemOrder: getSaveableDashboardOrder(dashboardSetting, items),
      cardColors: { ...dashboardSetting.cardColors },
      articleLimits: { ...dashboardSetting.articleLimits },
      articleDisplayFilters: { ...dashboardSetting.articleDisplayFilters },
    };

    setDashboardUpdatingId(item.id);
    setDashboardToggleError(null);
    try {
      const response = await monitoringApi.post({
        command: "save_dashboard_setting",
        userId,
        visibleItemIds: nextSetting.visibleItemIds,
        itemOrder: nextSetting.itemOrder,
        cardColors: nextSetting.cardColors,
        articleLimits: nextSetting.articleLimits,
        articleDisplayFilters: nextSetting.articleDisplayFilters,
      });
      if (response?.code !== "200") {
        throw new Error(response?.response?.error_msg ?? "대시보드 표시 설정을 저장하지 못했습니다.");
      }
      setDashboardSetting(normalizeDashboardSetting(response.response?.dashboardSetting ?? nextSetting));
    } catch (error) {
      setDashboardToggleError(error instanceof Error ? error.message : "대시보드 표시 설정을 저장하지 못했습니다.");
    } finally {
      setDashboardUpdatingId(null);
    }
  };

  const dashboardToggleDisabled = !dashboardSetting || Boolean(dashboardSettingError) || dashboardSettingLoading;
  const tableCaption = dashboardSettingError || dashboardToggleError
    ? `${filteredItems.length}개 표시 · ${dashboardSettingError ?? dashboardToggleError}`
    : `${filteredItems.length}개 표시 · 상세보기는 단일 모니터링 작업 화면으로 이동합니다.`;

  return (
    <Box sx={pageSx}>
      <Box sx={pageContentSx}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.4} alignItems={{ md: "flex-start" }} sx={pageHeaderSx}>
          <Box>
            <Typography sx={pageTitleSx}>모니터링 관리</Typography>
            <Typography sx={{ ...pageSubtitleSx, color: loadError ? "#b91c1c" : "#2E3341" }}>
              {loading ? "목록을 불러오는 중입니다." : loadError ?? "등록된 모니터링의 상태와 실행 정책을 빠르게 점검합니다."}
            </Typography>
          </Box>

          <Stack spacing={0.75} alignItems={{ xs: "stretch", md: "flex-end" }} sx={{ pt: { md: 1.75 } }}>
            <Button
              startIcon={<Box component="img" src={monitoringListIcons.add} alt="" aria-hidden sx={primaryButtonIconSx} />}
              variant="contained"
              sx={primaryButtonSx}
              onClick={() => navigate("/monitoringCreate")}
            >
              새 모니터링
            </Button>
          </Stack>
        </Stack>

        <Paper ref={panelRef} variant="outlined" sx={tablePanelSx}>
          <Box sx={tableTitleBarSx}>
            <Box>
              <Typography sx={tableTitleSx}>관리 대상 모니터링</Typography>
              <Typography sx={{ ...tableCaptionSx, color: dashboardSettingError || dashboardToggleError ? "#b91c1c" : "#6F7279" }}>
                {tableCaption}
              </Typography>
            </Box>
          </Box>

          <Box sx={tableBodySx}>
            <Box
              data-testid="monitoring-list-layout"
              data-layout={layout}
              sx={tableContentSx}
            >
              <Box
                data-testid="monitoring-list-filters"
                data-layout={layout}
                sx={filterBarSx(layout)}
              >
                <Stack direction={isTableLayout ? "row" : "column"} spacing={1.4} alignItems={isTableLayout ? "center" : "stretch"}>
                  <FilterSelectLabel label="상태" stacked={!isTableLayout}>
                    <TextField
                      select
                      size="small"
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                      sx={selectFieldSx(layout)}
                    >
                      <MenuItem value="ALL">전체</MenuItem>
                      <MenuItem value="RUNNING">실행 중</MenuItem>
                      <MenuItem value="ACTIVE">활성</MenuItem>
                      <MenuItem value="PAUSED">중지</MenuItem>
                    </TextField>
                  </FilterSelectLabel>
                  <FilterSelectLabel label="유형" stacked={!isTableLayout}>
                    <TextField
                      select
                      size="small"
                      value={typeFilter}
                      onChange={(event) => setTypeFilter(event.target.value as TypeFilter)}
                      sx={selectFieldSx(layout)}
                    >
                      <MenuItem value="ALL">전체</MenuItem>
                      <MenuItem value="KEYWORD">키워드</MenuItem>
                      <MenuItem value="PRESS_RELEASE">보도자료</MenuItem>
                    </TextField>
                  </FilterSelectLabel>
                </Stack>
                <TextField
                  size="small"
                  placeholder="모니터링명, 설명, 유형 검색"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box component="img" src={monitoringListIcons.search} alt="" aria-hidden sx={searchIconSx} />
                      </InputAdornment>
                    ),
                  }}
                  sx={searchFieldSx(layout)}
                />
              </Box>

              <Box sx={tableGridSx}>
                {isTableLayout ? (
                  <Box data-testid="monitoring-table-head" sx={tableHeadSx(layout)}>
                    <Typography sx={{ ...columnHeadSx, textAlign: "center" }}>상태</Typography>
                    <Typography sx={columnHeadSx}>모니터링</Typography>
                    <Typography sx={columnHeadSx}>유형</Typography>
                    <Typography sx={columnHeadSx}>실행 정책</Typography>
                    <Typography sx={columnHeadSx}>기능/알림</Typography>
                    <Typography sx={columnHeadSx}>최근 실행</Typography>
                    <Typography sx={{ ...columnHeadSx, textAlign: "center" }}>대시보드 표시</Typography>
                    <Typography sx={{ ...columnHeadSx, textAlign: "center" }}>작업</Typography>
                  </Box>
                ) : null}

                {loading ? (
                  <Typography sx={emptyStateSx}>목록을 불러오는 중입니다.</Typography>
                ) : filteredItems.length === 0 ? (
                  <Typography sx={emptyStateSx}>조건에 맞는 모니터링이 없습니다.</Typography>
                ) : (
                  filteredItems.map((item) => (
                    <MonitoringTableRow
                      key={item.id}
                      item={item}
                      layout={layout}
                      statusUpdating={statusUpdatingId === item.id}
                      dashboardVisible={dashboardVisibleIds.has(getDashboardItemId(item))}
                      dashboardToggleDisabled={dashboardToggleDisabled}
                      dashboardUpdating={dashboardUpdatingId === item.id}
                      onStatusClick={() => setStatusDialogItem(item)}
                      onDashboardToggle={(checked) => handleDashboardToggle(item, checked)}
                      onOpen={() => navigate(buildMonitoringDetailUrl({ monitoringId: item.id, tab: "articles" }))}
                      onEdit={() => navigate("/monitoringCreate", { state: { mode: "edit", monitoringId: item.id, monitoring: item } })}
                    />
                  ))
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

      <Dialog
        open={Boolean(statusDialogItem)}
        onClose={() => (statusUpdatingId ? undefined : setStatusDialogItem(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={dialogTitleSx}>상태를 변경할까요?</DialogTitle>
        <DialogContent>
          <Typography sx={dialogTextSx}>
            {statusDialogItem
              ? statusDialogExpired
                ? `"${statusDialogItem.name}" 모니터링은 실행 기간이 만료되었습니다. 종료일을 수정한 뒤 활성화할 수 있습니다.`
                : `"${statusDialogItem.name}" 모니터링을 ${statusDialogItem.status === "ACTIVE" ? "중지" : "활성"} 상태로 변경합니다.`
              : ""}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.2 }}>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            disabled={Boolean(statusUpdatingId)}
            onClick={() => setStatusDialogItem(null)}
          >
            취소
          </Button>
          <Button
            variant="contained"
            sx={primaryButtonSx}
            disabled={Boolean(statusUpdatingId)}
            onClick={handleStatusToggleConfirm}
          >
            {statusUpdatingId ? "변경 중..." : statusDialogExpired ? "확인" : "변경"}
          </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

const FilterSelectLabel = ({ label, children, stacked = false }: { label: string; children: ReactNode; stacked?: boolean }) => (
  <Stack direction={stacked ? "column" : "row"} spacing={stacked ? 0.45 : 0.9} alignItems={stacked ? "stretch" : "center"}>
    <Typography sx={filterLabelSx}>{label}</Typography>
    {children}
  </Stack>
);

const MonitoringCardField = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={cardFieldSx}>
    <Typography sx={cardFieldLabelSx}>{label}</Typography>
    <Box sx={cardFieldValueSx}>{children}</Box>
  </Box>
);

const DashboardVisibilitySwitch = ({
  item,
  dashboardVisible,
  dashboardToggleDisabled,
  dashboardUpdating,
  onDashboardToggle,
}: {
  item: MonitoringListItem;
  dashboardVisible: boolean;
  dashboardToggleDisabled: boolean;
  dashboardUpdating: boolean;
  onDashboardToggle: (checked: boolean) => void;
}) => (
  <Tooltip title={dashboardUpdating ? "저장 중" : "대시보드 표시 여부"}>
    <span>
      <Switch
        checked={dashboardVisible}
        disabled={dashboardToggleDisabled || dashboardUpdating}
        onChange={(event) => onDashboardToggle(event.target.checked)}
        inputProps={{ "aria-label": `${item.name} 대시보드 표시` }}
        sx={dashboardSwitchSx}
      />
    </span>
  </Tooltip>
);

const MonitoringFeatureSummary = ({
  item,
  notificationLabel,
  showEmptyState = false,
}: {
  item: MonitoringListItem;
  notificationLabel: string;
  showEmptyState?: boolean;
}) => {
  const hasFeatureChips = item.sentimentEnabled || (item.notificationEnabled && item.notifyOnNegative);

  return (
    <Box sx={featureCellSx}>
      {hasFeatureChips ? (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
          {item.sentimentEnabled ? <FeatureChip label="긍부정" /> : null}
          {item.notificationEnabled && item.notifyOnNegative ? <FeatureChip label="부정 알림" tone="notice" /> : null}
        </Stack>
      ) : showEmptyState && !notificationLabel ? (
        <Typography sx={notificationTextSx}>사용 안 함</Typography>
      ) : null}
      {notificationLabel ? <Typography sx={notificationTextSx}>{notificationLabel}</Typography> : null}
    </Box>
  );
};

const MonitoringTableRow = ({
  item,
  layout,
  statusUpdating,
  dashboardVisible,
  dashboardToggleDisabled,
  dashboardUpdating,
  onStatusClick,
  onDashboardToggle,
  onOpen,
  onEdit,
}: {
  item: MonitoringListItem;
  layout: MonitoringListLayout;
  statusUpdating: boolean;
  dashboardVisible: boolean;
  dashboardToggleDisabled: boolean;
  dashboardUpdating: boolean;
  onStatusClick: () => void;
  onDashboardToggle: (checked: boolean) => void;
  onOpen: () => void;
  onEdit: () => void;
}) => {
  const notificationLabel = item.notificationEnabled ? getNotificationAudienceLabel(item) : "";

  if (layout === "card") {
    return (
      <Box component="article" aria-label={`${item.name} 모니터링 정보`} sx={cardRowSx}>
        <MonitoringCardField label="상태">
          <StatusTextButton item={item} disabled={statusUpdating} onClick={onStatusClick} />
        </MonitoringCardField>

        <MonitoringCardField label="모니터링">
          <Box sx={monitoringCardCellSx} onClick={onOpen}>
            <Stack direction="row" spacing={0.55} alignItems="center" sx={{ minWidth: 0, flexWrap: "wrap", rowGap: 0.35 }}>
              <Typography sx={monitorTitleSx}>{item.name}</Typography>
              {isNewMonitoring(item.createdAt) ? <Chip label="New" size="small" sx={newBadgeSx} /> : null}
            </Stack>
            <Typography sx={monitorDescSx}>{item.description || "설명 없음"}</Typography>
          </Box>
        </MonitoringCardField>

        <MonitoringCardField label="유형">
          <Typography sx={cardValueTextSx}>{getTypeLabel(item.monitoringType)}</Typography>
        </MonitoringCardField>

        <MonitoringCardField label="실행 정책">
          <Box sx={policyPillsSx}>
            <PolicyPill label={`${item.intervalMinutes}분 주기`} />
            <PolicyPill label={`${item.retentionDays}일 보관`} />
            <PolicyPill label={formatMonitoringActivePeriod(item)} muted={isMonitoringExpired(item)} />
            {!item.notificationEnabled ? <PolicyPill label="알림 미사용" muted /> : null}
          </Box>
        </MonitoringCardField>

        <MonitoringCardField label="기능/알림">
          <MonitoringFeatureSummary item={item} notificationLabel={notificationLabel} showEmptyState />
        </MonitoringCardField>

        <MonitoringCardField label="최근 실행">
          <Typography sx={cardValueTextSx}>{formatRecentRun(item.lastExecutedAt)}</Typography>
        </MonitoringCardField>

        <MonitoringCardField label="대시보드 표시">
          <Box sx={dashboardSwitchCardSx}>
            <DashboardVisibilitySwitch
              item={item}
              dashboardVisible={dashboardVisible}
              dashboardToggleDisabled={dashboardToggleDisabled}
              dashboardUpdating={dashboardUpdating}
              onDashboardToggle={onDashboardToggle}
            />
          </Box>
        </MonitoringCardField>

        <MonitoringCardField label="작업">
          <Stack direction="row" sx={cardActionCellSx}>
            <Button variant="outlined" sx={smallActionButtonSx} onClick={onOpen}>
              상세
            </Button>
            <Button variant="outlined" sx={smallActionButtonSx} onClick={onEdit}>
              수정
            </Button>
          </Stack>
        </MonitoringCardField>
      </Box>
    );
  }

  return (
    <Box sx={tableRowSx}>
      <Box sx={rowGridSx(layout)}>
        <Box sx={statusColumnSx}>
          <StatusTextButton item={item} disabled={statusUpdating} onClick={onStatusClick} />
        </Box>

        <Box sx={monitoringCellSx} onClick={onOpen}>
          <Stack direction="row" spacing={0.55} alignItems="center" sx={{ minWidth: 0, flexWrap: "wrap", rowGap: 0.35 }}>
            <Typography sx={monitorTitleSx}>{item.name}</Typography>
            {isNewMonitoring(item.createdAt) ? <Chip label="New" size="small" sx={newBadgeSx} /> : null}
          </Stack>
          <Typography sx={monitorDescSx}>{item.description || "설명 없음"}</Typography>
        </Box>

        <Typography sx={typeTextSx}>{getTypeLabel(item.monitoringType)}</Typography>

        <Box sx={policyCellSx}>
          <Box sx={policyPillsSx}>
            <PolicyPill label={`${item.intervalMinutes}분 주기`} />
            <PolicyPill label={`${item.retentionDays}일 보관`} />
            <PolicyPill label={formatMonitoringActivePeriod(item)} muted={isMonitoringExpired(item)} />
            {!item.notificationEnabled ? <PolicyPill label="알림 미사용" muted /> : null}
          </Box>
        </Box>

        <MonitoringFeatureSummary item={item} notificationLabel={notificationLabel} />

        <Typography sx={recentTextSx(layout)}>{formatRecentRun(item.lastExecutedAt)}</Typography>

        <Box sx={dashboardSwitchCellSx}>
          <DashboardVisibilitySwitch
            item={item}
            dashboardVisible={dashboardVisible}
            dashboardToggleDisabled={dashboardToggleDisabled}
            dashboardUpdating={dashboardUpdating}
            onDashboardToggle={onDashboardToggle}
          />
        </Box>

        <Stack direction="row" sx={actionCellSx}>
          <Button variant="outlined" sx={smallActionButtonSx} onClick={onOpen}>
            상세
          </Button>
          <Button variant="outlined" sx={smallActionButtonSx} onClick={onEdit}>
            수정
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

const StatusTextButton = ({
  item,
  disabled = false,
  compact = false,
  onClick,
}: {
  item: MonitoringListItem;
  disabled?: boolean;
  compact?: boolean;
  onClick?: () => void;
}) => {
  const runtimeStatus = getRuntimeStatus(item);
  const actionLabel = item.status === "ACTIVE" ? "중지" : "활성";

  return (
    <Tooltip title={`${runtimeStatus.label} · 클릭하여 ${actionLabel}`}>
      <span>
        <Button
          variant="text"
          disabled={disabled}
          aria-label={`${item.name} 상태 변경`}
          onClick={(event) => {
            event.stopPropagation();
            onClick?.();
          }}
          sx={statusTextButtonSx(runtimeStatus.tone, compact)}
        >
          {runtimeStatus.label}
        </Button>
      </span>
    </Tooltip>
  );
};

const PolicyPill = ({ label, muted = false }: { label: string; muted?: boolean }) => (
  <Box sx={policyPillSx(muted)}>{label}</Box>
);

const FeatureChip = ({ label, tone = "default" }: { label: string; tone?: "default" | "notice" }) => (
  <Chip label={label} size="small" sx={featureChipSx(tone)} />
);

const listFontFamily = "\"Pretendard\", sans-serif";

const pageSx = {
  minHeight: "100%",
  minWidth: 0,
  bgcolor: "#F2F4F8",
  boxSizing: "border-box",
  px: { xs: 2, md: 3 },
  py: { xs: 2.5, md: 5 },
  fontFamily: listFontFamily,
  letterSpacing: 0,
  "& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiChip-root": {
    fontFamily: listFontFamily,
  },
};
const pageContentSx = { width: "100%", minWidth: 0, maxWidth: 1518, mx: "auto" };
const pageHeaderSx = { minHeight: { md: 60 }, mb: { xs: 2, md: 5 } };
const pageTitleSx = {
  color: "#2E3341",
  fontSize: 24,
  fontWeight: 700,
  lineHeight: "32px",
  letterSpacing: "-0.015em",
};
const pageSubtitleSx = {
  mt: 0.5,
  color: "#2E3341",
  fontSize: 16,
  fontWeight: 400,
  lineHeight: "24px",
  letterSpacing: "-0.015em",
};
const tablePanelSx = {
  width: "100%",
  minWidth: 0,
  maxWidth: 1518,
  borderRadius: "12px",
  border: "none",
  bgcolor: "#fff",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
};
const tableTitleBarSx = {
  height: 74,
  boxSizing: "border-box",
  px: 3,
  pt: 3,
  pb: 1,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
};
const tableTitleSx = { color: "#000000", fontSize: 16, fontWeight: 600, lineHeight: "22px" };
const tableCaptionSx = { mt: 0, fontSize: 13, fontWeight: 400, lineHeight: "20px", letterSpacing: "-0.015em" };
const tableBodySx = {
  boxSizing: "border-box",
  p: { xs: 2, md: 3 },
  minWidth: 0,
};
const tableContentSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 1.5,
  minWidth: 0,
  width: "100%",
};
const tableGridSx = {
  minWidth: 0,
  width: "100%",
};
const filterBarSx = (layout: MonitoringListLayout) => ({
  display: "flex",
  flexDirection: layout === "card" ? "column" : "row",
  flexWrap: layout === "compact-table" ? "wrap" : "nowrap",
  gap: 1.5,
  alignItems: layout === "card" ? "stretch" : "center",
  justifyContent: layout === "card" ? "flex-start" : "space-between",
  boxSizing: "border-box",
  minHeight: 48,
  minWidth: 0,
});
const filterLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px", whiteSpace: "nowrap" };
const tableHeadSx = (layout: MonitoringListLayout) => ({
  display: "grid",
  gridTemplateColumns: layout === "table" ? figmaColumnTemplate : compactColumnTemplate,
  gap: 0,
  alignItems: "center",
  py: 0,
  minWidth: 0,
  height: 40,
  borderTop: "1px solid #2E3341",
  bgcolor: "#F2F5FD",
});
const columnHeadSx = {
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  letterSpacing: "-0.015em",
  textAlign: "center",
};
const tableRowSx = {
  py: 0,
  borderBottom: "1px solid #F2F2F2",
  "&:last-of-type": { borderBottom: "none" },
  "&:hover": { bgcolor: "#FBFCFF" },
};
const rowGridSx = (layout: MonitoringListLayout) => ({
  display: "grid",
  gridTemplateColumns: layout === "table" ? figmaColumnTemplate : compactColumnTemplate,
  gap: 0,
  alignItems: "stretch",
  minWidth: 0,
  minHeight: 80,
  "& > *": {
    boxSizing: "border-box",
    minHeight: 80,
    px: layout === "table" ? 2 : 1,
    py: 1.5,
  },
});
const statusColumnSx = { display: "flex", alignItems: "center", justifyContent: "center" };
const monitoringCellSx = { minWidth: 0, cursor: "pointer", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start" };
const monitorTitleSx = { color: "#2E3341", fontSize: 16, fontWeight: 600, lineHeight: "24px", letterSpacing: "-0.015em", overflowWrap: "anywhere" };
const monitorDescSx = { mt: 0, color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px", letterSpacing: "-0.015em", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" };
const typeTextSx = { color: "#2E3341", fontSize: 16, fontWeight: 500, lineHeight: "24px", display: "flex", alignItems: "center", justifyContent: "center", whiteSpace: "nowrap" };
const policyCellSx = { minWidth: 0, display: "flex", alignItems: "center" };
const policyPillsSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  alignContent: "center",
  justifyContent: "flex-start",
  columnGap: "6px",
  rowGap: "4px",
  width: "100%",
};
const featureCellSx = { minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start" };
const notificationTextSx = { mt: 0.45, color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px", letterSpacing: "-0.015em" };
const recentTextSx = (layout: MonitoringListLayout) => ({
  color: "#2E3341",
  fontSize: layout === "compact-table" ? 13 : 16,
  fontWeight: 400,
  lineHeight: layout === "compact-table" ? "20px" : "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  letterSpacing: "-0.015em",
  whiteSpace: "nowrap",
});
const emptyStateSx = { p: 3.2, color: "#6F7279", fontSize: 13, textAlign: "center" };
const cardRowSx = {
  display: "grid",
  gap: 1.5,
  minWidth: 0,
  p: { xs: 2, md: 2.5 },
  border: "1px solid #E4E8F0",
  borderRadius: "10px",
  bgcolor: "#fff",
  "& + &": { mt: 1.25 },
};
const cardFieldSx = { display: "flex", flexDirection: "column", gap: 0.45, minWidth: 0 };
const cardFieldLabelSx = { color: "#6F7279", fontSize: 13, fontWeight: 600, lineHeight: "20px", letterSpacing: "-0.015em" };
const cardFieldValueSx = { minWidth: 0 };
const monitoringCardCellSx = { ...monitoringCellSx, width: "100%" };
const cardValueTextSx = { color: "#2E3341", fontSize: 16, fontWeight: 400, lineHeight: "24px", letterSpacing: "-0.015em", overflowWrap: "anywhere" };
const dashboardSwitchCardSx = { display: "flex", alignItems: "center", minHeight: 24 };
const cardActionCellSx = { flexWrap: "wrap", alignItems: "center", columnGap: "6px", rowGap: "6px" };
const searchFieldSx = (layout: MonitoringListLayout) => ({
  width: layout === "card" ? "100%" : 400,
  minWidth: 0,
  ...(layout === "card" ? {} : { flex: "0 1 400px", maxWidth: "100%", ml: "auto" }),
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: "#fff",
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "24px",
    height: 48,
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CDD9" },
  "& .MuiOutlinedInput-input": {
    py: 1.2,
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "24px",
    letterSpacing: "-0.015em",
    "&::placeholder": { color: "#A3A3A3", opacity: 1 },
  },
});
const selectFieldSx = (layout: MonitoringListLayout) => ({
  width: layout === "table" ? 160 : layout === "compact-table" ? 120 : "100%",
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: "#fff",
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "24px",
    height: 48,
    position: "relative",
    "&::after": {
      content: "\"\"",
      position: "absolute",
      right: 16,
      top: "50%",
      width: 12,
      height: 7,
      transform: "translateY(-50%)",
      backgroundImage: `url(${monitoringListIcons.selectArrow})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      backgroundPosition: "center",
      pointerEvents: "none",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CDD9" },
  "& .MuiSelect-select": {
    py: 1.2,
    pr: "42px !important",
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "24px",
    letterSpacing: "-0.015em",
  },
  "& .MuiSelect-icon": { display: "none" },
});
const primaryButtonSx = {
  minWidth: "auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  boxSizing: "border-box",
  borderRadius: "8px",
  bgcolor: "#2E3341",
  color: "#fff",
  height: 40,
  minHeight: 40,
  px: 2,
  py: 0,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: "-0.015em",
  boxShadow: "none",
  "& .MuiButton-startIcon": { m: 0, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  "&:hover": { bgcolor: "#232734", boxShadow: "none" },
};
const primaryButtonIconSx = { width: 16, height: 16, objectFit: "contain", display: "block" };
const outlineButtonSx = { borderRadius: "8px", borderColor: "#DDE0EA", color: "#2E3341", fontSize: 14, fontWeight: 600, lineHeight: "14px", letterSpacing: "-0.015em", bgcolor: "#fff", boxShadow: "none", "&:hover": { borderColor: "#C8CDD9", bgcolor: "#F8FAFF" } };
const smallActionButtonSx = { ...outlineButtonSx, minWidth: 48, width: 48, height: 32, px: 1.5, whiteSpace: "nowrap" };
const actionCellSx = {
  flexWrap: "wrap",
  alignItems: "center",
  alignContent: "center",
  justifyContent: "center",
  columnGap: "6px",
  rowGap: "6px",
};
const searchIconSx = { width: 18, height: 18, objectFit: "contain", display: "block" };
const statusTextButtonSx = (tone: RuntimeStatusTone, compact: boolean) => ({
  minWidth: compact ? 0 : 54,
  px: compact ? 0.2 : 0.4,
  py: 0.2,
  color: tone === "running" ? "#F07000" : tone === "ready" ? "#0F5FE2" : "#2E3341",
  fontSize: compact ? 14 : 16,
  fontWeight: 500,
  lineHeight: "24px",
  textDecoration: "none",
  "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
  "&.Mui-disabled": {
    color: tone === "running" ? "#F07000" : tone === "ready" ? "#0F5FE2" : "#2E3341",
    opacity: 0.5,
  },
});
const newBadgeSx = { height: 24, borderRadius: "16px", border: "1px solid #5D88F1", bgcolor: "#fff", color: "#5D88F1", fontSize: 13, fontWeight: 500, lineHeight: "13px", "& .MuiChip-label": { px: 1 } };
const policyPillSx = (muted: boolean) => ({
  height: 24,
  display: "inline-flex",
  flex: "0 0 auto",
  alignItems: "center",
  justifyContent: "center",
  px: 1,
  py: 0,
  borderRadius: "16px",
  bgcolor: muted ? "#F4F5F6" : "#EEEFF0",
  color: muted ? "#6F7279" : "#2E3341",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
});
const featureChipSx = (tone: "default" | "notice") => ({
  height: 24,
  borderRadius: "16px",
  bgcolor: tone === "notice" ? "#FFF1F1" : "#F0F4FF",
  color: tone === "notice" ? "#E54848" : "#2E3341",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
  "& .MuiChip-label": { px: 1 },
});
const dashboardSwitchCellSx = { display: "flex", justifyContent: "center", alignItems: "center" };
const dashboardSwitchSx = {
  width: 40,
  height: 24,
  p: 0,
  "& .MuiSwitch-switchBase": {
    p: "4px",
    color: "#fff",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": { bgcolor: "#5D88F1", opacity: 1 },
    },
    "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.45 },
  },
  "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "none" },
  "& .MuiSwitch-track": { borderRadius: "20px", bgcolor: "#DDE0EA", opacity: 1 },
};
const dialogTitleSx = { fontSize: 17, fontWeight: 850, color: "#172033" };
const dialogTextSx = { color: "#475467", fontSize: 14, lineHeight: 1.65 };

export default NewMonitoringListPage;
